import { AxiosError } from "axios";
import { toast } from "sonner";
import type { ExternalToast } from "sonner";
import { ErrorSubcode } from "@/types";
import {
  type ErrorEntry,
  type ErrorSuffix,
  type FeatureArea,
  defaultMessages,
  deliverableEditFailureMessages,
  featureMessages,
} from "@/lib/errorMessages";

const GENERIC_FALLBACK: ErrorEntry = {
  message: "Something went wrong.",
  suffix: "support",
  severity: "error",
};

const SUFFIX_TEXT: Record<ErrorSuffix, string> = {
  support: " Try again later or contact support@meetwithfocal.com.",
  retry: " Please try again.",
  none: "",
};

/** Narrow an unknown value to a known {@link ErrorSubcode}. */
export function isValidSubcode(value: unknown): value is ErrorSubcode {
  return (
    typeof value === "string" &&
    Object.values(ErrorSubcode).includes(value as ErrorSubcode)
  );
}

/** Extract subcode and statusCode from any error shape */
export function parseApiError(error: unknown): {
  subcode: ErrorSubcode;
  statusCode: number;
} {
  // Handle AxiosError
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    const subcode = isValidSubcode(data?.subcode)
      ? data.subcode
      : ErrorSubcode.UNKNOWN_ERROR;
    // Prefer normalized statusCode from response body (set by proxy),
    // fall back to HTTP status, then 500
    const statusCode =
      typeof data?.statusCode === "number"
        ? data.statusCode
        : (error.response?.status ?? 500);
    return { subcode, statusCode };
  }

  // Handle plain objects with response shape (e.g., from fetch or non-Axios clients)
  if (error && typeof error === "object") {
    const resp = (error as Record<string, any>).response;
    const data = resp?.data ?? error;
    const statusCode = typeof resp?.status === "number"
      ? resp.status
      : (typeof data?.statusCode === "number" ? data.statusCode : 500);
    const subcode = isValidSubcode(data?.subcode)
      ? data.subcode
      : ErrorSubcode.UNKNOWN_ERROR;
    return { subcode, statusCode };
  }

  return { subcode: ErrorSubcode.UNKNOWN_ERROR, statusCode: 500 };
}

export interface MissingIntegrationPermission {
  id: string;
  label: string;
}

/**
 * The permissions a connect was rejected for, or null when the error is not an
 * `INTEGRATION_PERMISSIONS_MISSING` rejection.
 */
export function getMissingPermissions(
  error: unknown,
): MissingIntegrationPermission[] | null {
  if (parseApiError(error).subcode !== ErrorSubcode.INTEGRATION_PERMISSIONS_MISSING) {
    return null;
  }
  const data =
    error instanceof AxiosError
      ? error.response?.data
      : ((error as Record<string, any>)?.response?.data ?? error);
  const missing = data?.details?.missingPermissions;
  if (!Array.isArray(missing)) return [];
  return missing.filter(
    (p): p is MissingIntegrationPermission =>
      typeof p?.id === "string" && typeof p?.label === "string",
  );
}

/**
 * Subcodes that are catch-all/generic — their default messages don't describe
 * what operation failed. A caller-provided fallback should override these.
 */
const GENERIC_SUBCODES = new Set<ErrorSubcode>([
  ErrorSubcode.OPERATION_FAILED,
  ErrorSubcode.UNKNOWN_ERROR,
  ErrorSubcode.INTERNAL_ERROR,
]);

/**
 * Resolve the ErrorEntry: feature-specific -> default -> generic fallback.
 * `useFallback` is true when the subcode is generic AND no feature-specific entry exists.
 */
function resolveErrorEntry(
  subcode: ErrorSubcode,
  feature?: FeatureArea,
): { entry: ErrorEntry; useFallback: boolean } {
  if (feature) {
    const featureEntry = featureMessages[feature]?.[subcode];
    if (featureEntry) return { entry: featureEntry, useFallback: false };
  }
  const defaultEntry = defaultMessages[subcode];
  if (defaultEntry) {
    return { entry: defaultEntry, useFallback: GENERIC_SUBCODES.has(subcode) };
  }
  return { entry: GENERIC_FALLBACK, useFallback: true };
}

/** Build the full user-facing message string */
function buildMessage(entry: ErrorEntry): string {
  const suffix = SUFFIX_TEXT[entry.suffix ?? "none"];
  return entry.message + suffix;
}

/**
 * Get the user-facing message string without showing a toast (for inline display).
 * Pass `fallback` to provide an operation-specific message for generic subcodes
 * (e.g., OPERATION_FAILED, UNKNOWN_ERROR) where the registry has no feature-specific entry.
 */
export function getErrorMessage(
  error: unknown,
  feature?: FeatureArea,
  fallback?: string,
): string {
  const { subcode } = parseApiError(error);
  const { entry, useFallback } = resolveErrorEntry(subcode, feature);
  if (useFallback && fallback) return fallback;
  return buildMessage(entry);
}

/**
 * Parse error, resolve message, show toast.
 * Pass `fallback` to provide an operation-specific message for generic subcodes
 * (e.g., OPERATION_FAILED, UNKNOWN_ERROR) where the registry has no feature-specific entry.
 * When the subcode has a feature-specific entry (e.g., ALREADY_EXISTS for "phone"),
 * the registry message is used and the fallback is ignored.
 */
export function showErrorToast(
  error: unknown,
  feature?: FeatureArea,
  fallback?: string,
  options?: ExternalToast,
): void {
  const { subcode } = parseApiError(error);
  const { entry, useFallback } = resolveErrorEntry(subcode, feature);

  if (useFallback && fallback) {
    toast.error(fallback, options);
    return;
  }

  const message = buildMessage(entry);
  if (entry.severity === "warning") {
    toast.warning(message, options);
  } else {
    toast.error(message, options);
  }
}

/** Select recovery copy from structured fields, never upstream error prose. */
export function getDeliverableEditFailure(error: unknown): string {
  const { subcode, statusCode } = parseApiError(error);
  const record = error as {
    response?: { data?: { reason?: unknown; detail?: { reason?: unknown } } };
  } | null;
  const data = record?.response?.data;
  const reason = data?.reason ?? data?.detail?.reason;
  if (reason === "AGENT_LIMIT_REACHED")
    return deliverableEditFailureMessages.tooLarge;
  if (statusCode === 401 || statusCode === 403)
    return deliverableEditFailureMessages.access;
  if (
    reason === "INSUFFICIENT_DATA" ||
    subcode === ErrorSubcode.MISSING_FIELD
  ) {
    return deliverableEditFailureMessages.missingData;
  }
  if (
    reason === "DOCUMENT_SERVICE_UNREACHABLE" ||
    subcode === ErrorSubcode.EXTERNAL_SERVICE_ERROR ||
    statusCode === 503 ||
    statusCode === 504
  ) {
    return deliverableEditFailureMessages.unavailable;
  }
  if (
    reason === "INSPECTION_FAILED" ||
    reason === "VALIDATION_FAILED" ||
    reason === "REPAIR_EXHAUSTED" ||
    statusCode === 422 ||
    subcode === ErrorSubcode.VALIDATION_ERROR
  ) {
    return deliverableEditFailureMessages.validation;
  }
  return deliverableEditFailureMessages.failed;
}
